import React, { useMemo, useRef, useState } from 'react';
import { graphql } from 'gatsby';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import MainSection100 from '../components/Layout/main-section-100';
import { MainSection7030 } from '../components/Layout/main-section-70-30';
import NewLayout from '../components/new_layout';
import Seo from '../components/seo';
import ReadyToTalk from '../components/v2023/ReadyToTalk/ready-to-talk';
import CardPost from '../components/v2023/UI/Card/card-post';
import { Dropdown } from '../components/v2024/Dropdown/dropdown';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

const KnowledgeResources = ({ data, pageContext }) => {
    const page = data.page;
    const tagCategories = data.tagCategory.edges;
    const tags = data.tags.edges;
    const highlights = data.highlights.edges;
    const genericPages = data.generic.edges;
    const lang = pageContext.langKey;
    const extraUrl = lang === 'es' ? '/es/' : '/';

    const elements = {
        li: ({ node, ...props }) => {
            return (
                <li {...props}>
                    <div>{props.children}</div>
                </li>
            );
        },
    };

    // Función para recoger el id numérico del id compuesto por strapi
    const getNumericId = (fullId) => {
        if (typeof fullId === 'number') return fullId;
        return parseInt(fullId?.split('_')[1]);
    };

    // Filtros por defecto
    const defaultFilters = {};
    tagCategories.map((tagCategory) => {
        const categoryId = getNumericId(tagCategory.node.id);
        defaultFilters[categoryId] = null;
    });

    // Creamos los filtros con los valores por defecto
    const [filters, setFilters] = useState(defaultFilters);

    const filtersRefs = useRef([]);
    // Restablecer los filtros
    const resetFiltersSuccessCases = () => {
        setFilters(defaultFilters);
        filtersRefs.current?.forEach((ref) => {
            if (ref) {
                ref.restartSelected();
            }
        });
    };

    // Función al seleccionar un valor de un dropdown
    const dropdownFilter = (data) => {
        const categoryTagId = data.node.resources_tag_category.id;
        // const tagId = getNumericId(data.node.id)
        const tag = data;
        setFilters((prevState) => ({
            ...prevState,
            [categoryTagId]: tag,
        }));
    };

    // Validamos si hay filtros puestos
    const areFiltersSet = () => {
        return Object.values(filters).some((filter) => filter !== null);
    };

    const genericPagesShow = useMemo(() => {
        const activeFilters = Object.values(filters).filter((selectedTag) => selectedTag !== null);

        if (activeFilters.length === 0) {
            return [...genericPages].reverse();
        }

        const requiredTagIds = new Set(activeFilters.map((tag) => getNumericId(tag.node.id)));
        const filteredPages = genericPages.filter((item) => {
            const pageTagIds = new Set(item.node.resources_tags.map((tag) => getNumericId(tag.id)));
            return Array.from(requiredTagIds).every((requiredId) => pageTagIds.has(requiredId));
        });

        return [...filteredPages].reverse();
    }, [filters, genericPages]);

    let [limitResources, setLimitResources] = useState(8);

    const showMoreResources = () => {
        if (limitResources < genericPagesShow.length) {
            setLimitResources(limitResources + 4);
        }
    };

    return (
        <NewLayout pageContext={pageContext} lang={pageContext?.langKey} translates={pageContext?.translates}>
            <Seo
                lang={lang}
                title={page.seo.title}
                description={page.seo.meta_description}
                translates={pageContext.translates}
                image={page.seo.image_2.localFile.publicURL}
            />
            <main className="main">
                <MainSection100>
                    <div className={'container__text'}>
                        <h1 className="hero hero--thin">{page.hero_title}</h1>
                        <div className="container__text__text">
                            <ReactMarkdown children={page.hero_description} rehypePlugins={[rehypeRaw]} />
                        </div>
                        <div className={'button__wrapper'}>
                            <a href={'https://www.tecalis.com/es/solicita-demo'} className={'button button--xl'}>
                                {page.hero_button_demo}
                            </a>
                        </div>
                    </div>
                </MainSection100>

                <MainSection100 sectionType={'color'} sectionColor={'gray'} grayNograph grayNoFrame>
                    <div>
                        <h1>{page.highlight_resources_title}</h1>
                        <br />
                        <br />
                        <div className="grid-lg-3">
                            {highlights.map((item, index) => (
                                <CardPost
                                    name={item.node.title}
                                    image={item.node.seo.image_2}
                                    url={extraUrl + item.node.url}
                                    description={item.node.seo.meta_description}
                                    // tag={item.node.tag}
                                    //tagHighlightSmall={item.node.tag}
                                />
                            ))}
                        </div>
                    </div>
                </MainSection100>

                <MainSection7030
                    children30={
                        <div className="container__text">
                            <h2>{page.filter_title}</h2>
                            <div className="dropdown-group">
                                {tagCategories.map((tagCategory, index) => {
                                    const tagCategoryId = getNumericId(tagCategory.node.id);
                                    const options = tags.filter(
                                        (tag) => tag.node.resources_tag_category?.id == tagCategoryId,
                                    );

                                    return (
                                        <Dropdown
                                            showValue={tagCategory.node.name}
                                            options={options}
                                            onSelectOption={dropdownFilter}
                                            ref={(el) => (filtersRefs.current[index] = el)}
                                        />
                                    );
                                })}
                                {areFiltersSet() && (
                                    <a className="link--small" onClick={() => resetFiltersSuccessCases()}>
                                        {page.filter_reset}
                                    </a>
                                )}
                            </div>
                        </div>
                    }
                    children70={
                        <>
                            {genericPagesShow.length === 0 && (
                                <div className="empty-state">
                                    <GatsbyImage
                                        alt={page.filter_no_found_image.alternativeText}
                                        image={getImage(page.filter_no_found_image.localFile)}
                                    />
                                    <ReactMarkdown children={page.filter_no_found_text} rehypePlugins={[rehypeRaw]} />
                                </div>
                            )}
                            {genericPagesShow.length !== 0 && (
                                <>
                                    <div class="container__content">
                                        <div className="grid-lg-2">
                                            {genericPagesShow.slice(0, limitResources).map((item) => {
                                                return (
                                                    <CardPost
                                                        key={item.node.url} // añade una key si estás en un map
                                                        name={item.node.title}
                                                        image={item.node.seo.image_2}
                                                        url={extraUrl + item.node.url}
                                                        description={item.node.description}
                                                        size={'lg'}
                                                    />
                                                );
                                            })}
                                        </div>
                                        {limitResources < genericPagesShow.length && (
                                            <div className="button-wrapper">
                                                <button
                                                    className="button button--secondary button--xl"
                                                    onClick={showMoreResources}
                                                >
                                                    {page.see_more_button}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    }
                />

                <MainSection100>
                    <div className={'container__text'}>
                        <ReactMarkdown children={page.description} rehypePlugins={[rehypeRaw]} components={elements} />
                        <div className="tile tile--white--nb">
                            <ReactMarkdown
                                children={page.description_card}
                                rehypePlugins={[rehypeRaw]}
                                components={elements}
                            />
                        </div>
                    </div>
                </MainSection100>

                <ReadyToTalk lang={lang} />
            </main>
        </NewLayout>
    );
};

export default KnowledgeResources;

export const knowledgeResourcesQuery = graphql`
	query ($langKey: String) {
		page: strapi2025LandingRecursos(locale: { eq: $langKey }) {
			seo {
				title
				meta_description
				image_2 {
					localFile {
                        publicURL
                    }
				}
			}
			hero_title
			hero_description
			hero_button_demo
			highlight_resources_title
			filter_title
			filter_no_found_text
			filter_no_found_image {
				localFile {
					childImageSharp {
						gatsbyImageData(quality: 100, placeholder: BLURRED, formats: [WEBP])
					}
				}
			}
			filter_reset
			see_more_button
			description
			description_card
		}
		highlights: allStrapiGenericLandingPage(
			filter: { resource: { eq: true }, locale: { eq: $langKey }, resource_highlight: { eq: true } }
			limit: 3
		) {
			edges {
				node {
					tag
					title
					description
					url
					hero_image {
						localFile {
							childImageSharp {
								gatsbyImageData(
									width: 417
									quality: 100
									placeholder: BLURRED
									formats: [WEBP]
									sizes: "(max-width: 768px) 50vw, 417px"
									outputPixelDensities: [1]
									breakpoints: [350]
								)
							}
						}
					}
					resources_tags {
						id
					}
					seo {
						title
						meta_description
						image_2 {
							localFile {
								childImageSharp {
									gatsbyImageData(
										width: 687
										quality: 100
										placeholder: BLURRED
										formats: [WEBP]
										sizes: "(max-width: 768px) 50vw, 417px"
										outputPixelDensities: [1]
										breakpoints: [350]
									)
								}
							}
						}
					}
				}
			}
		}
		generic: allStrapiGenericLandingPage(filter: { resource: { eq: true }, locale: { eq: $langKey } }) {
			edges {
				node {
					tag
					title
					description
					url
					hero_image {
						localFile {
							childImageSharp {
								gatsbyImageData(quality: 100, placeholder: BLURRED, formats: [WEBP])
							}
						}
					}
					resources_tags {
						id
					}
					seo {
						title
						meta_description
						image_2 {
							localFile {
								childImageSharp {
									gatsbyImageData(
										width: 687
										quality: 100
										placeholder: BLURRED
										formats: [WEBP]
										sizes: "(max-width: 768px) 50vw, 417px"
										outputPixelDensities: [1]
										breakpoints: [350]
									)
								}
							}
						}
					}
				}
			}
		}
		tagCategory: allStrapiResourcesTagCategories(filter: { locale: { eq: $langKey } }) {
			edges {
				node {
					id
					name
				}
			}
		}
		tags: allStrapiResourcesTags(filter: { locale: { eq: $langKey } }) {
			edges {
				node {
					id
					name
					resources_tag_category {
						id
					}
				}
			}
		}
	}
`;
